import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang, pickLocalized } from "@/contexts/LangContext";
import { useCart } from "@/contexts/CartContext";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setProduct(r.data));
  }, [slug]);

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 tracking-eyebrow text-[var(--eafg-muted)]">
        {t("common.loading")}
      </div>
    );
  }

  const name = pickLocalized(product, lang, "name");
  const description = pickLocalized(product, lang, "description");

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20 fade-in" data-testid="product-detail-page">
      <Link
        to="/tienda"
        className="inline-flex items-center gap-2 tracking-eyebrow text-[var(--eafg-muted)] hover:text-[var(--eafg-copper)] mb-10"
        data-testid="product-back-link"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> {t("product.back")}
      </Link>

      <div className="grid md:grid-cols-12 gap-10 md:gap-20">
        <div className="md:col-span-7 space-y-4">
          {(product.images || []).map((src, i) => (
            <div key={i} className="aspect-[4/5] overflow-hidden bg-[#efece8]">
              <img src={src} alt={`${name} ${i}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <div className="md:col-span-5">
          <div className="md:sticky md:top-32">
            <p className="tracking-eyebrow text-[var(--eafg-muted)]">
              {lang === "es" ? "Pieza única · hecha a mano" : "One of a kind · handmade"}
            </p>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight" data-testid="product-name">
              {name}
            </h1>
            <p className="text-2xl mt-6" data-testid="product-price">
              {Number(product.price).toFixed(2)} €
            </p>
            <p className="text-[var(--eafg-muted)] leading-relaxed mt-8" data-testid="product-description">
              {description}
            </p>

            <div className="mt-10 flex items-center gap-4">
              <div className="inline-flex items-center border border-[var(--eafg-ink)]">
                <button
                  className="px-3 py-3 hover:bg-[var(--eafg-ink)] hover:text-white transition-colors"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  data-testid="product-qty-decrement"
                >
                  <Minus size={14} strokeWidth={1.5} />
                </button>
                <span className="px-5 tabular-nums" data-testid="product-qty-value">
                  {qty}
                </span>
                <button
                  className="px-3 py-3 hover:bg-[var(--eafg-ink)] hover:text-white transition-colors"
                  onClick={() => setQty((q) => q + 1)}
                  data-testid="product-qty-increment"
                >
                  <Plus size={14} strokeWidth={1.5} />
                </button>
              </div>
              <button
                onClick={() => {
                  add(product, qty);
                  toast.success(t("product.addedToCart"));
                }}
                className="bg-[var(--eafg-copper)] text-white px-8 py-4 tracking-eyebrow hover:bg-[var(--eafg-copper-dark)] transition-colors flex-1"
                data-testid="product-add-to-cart"
              >
                {t("product.addToCart")}
              </button>
            </div>

            <div className="mt-10 pt-8 border-t border-[var(--eafg-line)] grid grid-cols-2 gap-6 text-sm">
              <div>
                <p className="tracking-eyebrow text-[var(--eafg-muted)]">{t("product.stock")}</p>
                <p className="mt-2">{product.stock} uds.</p>
              </div>
              <div>
                <p className="tracking-eyebrow text-[var(--eafg-muted)]">
                  {lang === "es" ? "Vistas" : "Views"}
                </p>
                <p className="mt-2">{product.views ?? 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
